import { useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import type { AssignmentCompletion, DirectoryAccount, Job, JobAssignment, NotifyTarget, UpdateAssignmentInput } from '@workflow/contracts'
import { Button, Dropdown, EmptyNote, FADE, Field, Icons, Loading, ProgressRing, Segmented, StateNote, StatusLabel, TextArea, TextField, cn } from '@workflow/ui'
import { describeError } from '@/lib/api/client'
import { useAssignPlayer, useAssignments, useMembers, useSetAssignmentCompletion, useSetJobStatus, useUpdateAssignment, useDeductAssignmentHours, useAssignmentHourAdjustments, useNotes, useSession } from '@/lib/api/hooks'
import { formatTimestamp } from '@/lib/format'
import { personDetails, personName, playerOf } from '@/lib/people'
import { PAYMENT_STATUS, partState, formatHours, formatMoney } from './jobsStatus'

/**
 * The verbs an assignment offers, by the state it is in. Dropping and
 * removing open a reason field before they act — the reason is the record,
 * and for a drop the API refuses to do without one. A completion can be
 * reopened, because a mark made on a player's behalf can be wrong.
 */
const VERBS: Record<AssignmentCompletion, readonly { target: AssignmentCompletion; verb: string; primary?: boolean; asksReason?: boolean }[]> = {
  assigned: [
    { target: 'in_progress', verb: 'Mark In Progress', primary: true },
    { target: 'completed', verb: 'Mark Completed' },
    { target: 'dropped', verb: 'Mark Dropped', asksReason: true },
    { target: 'removed', verb: 'Remove', asksReason: true },
  ],
  in_progress: [
    { target: 'completed', verb: 'Mark Completed', primary: true },
    { target: 'dropped', verb: 'Mark Dropped', asksReason: true },
    { target: 'removed', verb: 'Remove', asksReason: true },
  ],
  completed: [{ target: 'in_progress', verb: 'Reopen' }],
  dropped: [{ target: 'assigned', verb: 'Put Back on the Job' }],
  removed: [{ target: 'assigned', verb: 'Put Back on the Job' }],
}

/**
 * The crew of a job: the players on it, the hours each holds, what has been
 * completed of the work's size, and where each player stands. Assigning is a
 * decision about a person, so the player list is the active players only —
 * and one player sits on a job exactly once.
 */
export function JobCrew({ jobId, job }: { jobId: string; job: Job }) {
  const assignments = useAssignments(jobId)
  const members = useMembers()
  const [assigning, setAssigning] = useState(false)
  const crew = assignments.data ?? []

  const candidates: DirectoryAccount[] = members.data
    ? (members.data as DirectoryAccount[])
        .filter(
          (member) =>
            member.role === 'player' &&
            member.status === 'active' &&
            // Already at work, or finished for good — the server refuses both; the
            // list only offers players the job can actually take.
            !crew.some(
              (assignment) =>
                assignment.playerId === member.id &&
                (assignment.completion === 'assigned' ||
                  assignment.completion === 'in_progress' ||
                  assignment.completion === 'completed'),
            ),
        )
        // The clock reads here: hands on the clock first, here-and-now first
        // among them, off-the-clock hands last — never hidden, since work can
        // still be assigned when it must be.
        .sort((a, b) => {
          const rank = (member: DirectoryAccount) => (member.availability === 'active' ? (member.online ? 0 : 1) : 2)
          return rank(a) - rank(b) || personName(a).localeCompare(personName(b))
        })
    : []

  return (
    <div className="flex flex-col gap-3">
      <HoursSummary job={job} crew={crew} />

      {assigning ? (
        <AssignForm jobId={jobId} job={job} candidates={candidates} onDone={() => setAssigning(false)} onCancel={() => setAssigning(false)} />
      ) : (
        <div>
          <Button size="sm" variant="primary" onClick={() => setAssigning(true)} disabled={candidates.length === 0}>
            Assign Player
          </Button>
          {candidates.length === 0 ? (
            <p className="mt-1 text-caption text-muted-foreground">
              No active players left to assign — approve someone in People first.
            </p>
          ) : null}
        </div>
      )}

      <SettledBanner job={job} crew={crew} />

      {assignments.isLoading ? (
        <Loading label="Loading the crew" />
      ) : assignments.error ? (
        <StateNote title="The crew did not load." detail={describeError(assignments.error)} />
      ) : crew.length === 0 ? (
        <EmptyNote title="No one is on this job yet." detail="Assign a player to this job." />
      ) : (
        <ul className="flex flex-col">
          {crew.map((assignment) => (
            <AssignmentRow key={assignment.id} jobId={jobId} assignment={assignment} />
          ))}
        </ul>
      )}
    </div>
  )
}

/**
 * The hours story in one quiet line: a ring — the same mark the overview's
 * rows carry — beside the numbers, said once, in place. Oversubscription is
 * shown, not refused: a crew may deliberately hold more hands than the pool,
 * and the line says so rather than quietly allowing it.
 */
function HoursSummary({ job, crew }: { job: Job; crew: readonly JobAssignment[] }) {
  const pool = job.estimatedHours
  const active = crew.some((a) => a.completion === 'assigned' || a.completion === 'in_progress' || a.completion === 'completed')
  if (pool === null && !active && job.completedHours === 0) return null

  const over = pool !== null && job.assignedHours > pool
  const remaining = pool === null ? null : pool - job.assignedHours
  const ring =
    pool !== null && pool > 0
      ? { value: job.completedHours / pool, secondary: job.assignedHours / pool }
      : job.assignedHours > 0
        ? { value: job.completedHours / job.assignedHours, secondary: undefined }
        : null

  return (
    <div className="flex items-center gap-3">
      {ring ? (
        <ProgressRing
          size={26}
          value={ring.value}
          secondary={ring.secondary}
          label={`${formatHours(job.completedHours)} completed of ${formatHours(pool)} · ${formatHours(job.assignedHours)} assigned`}
        />
      ) : null}
      <p className="min-w-0 text-ui text-muted-foreground">
        <span className="font-medium text-foreground">{formatHours(job.completedHours)} completed</span>
        {pool !== null ? ` of ${formatHours(pool)} estimated` : ''}
        <span aria-hidden> · </span>
        <span className={over ? 'text-danger' : undefined}>
          {formatHours(job.assignedHours)} assigned{over ? ' — over the pool' : ''}
        </span>
        {remaining !== null && remaining > 0 ? ` · ${formatHours(remaining)} unassigned` : null}
      </p>
    </div>
  )
}

/**
 * The end of the hours story: when nobody is at work anymore but the work
 * happened, the job is ready to be completed — said here, where the crew
 * was just read, with the verb to do it.
 */
function SettledBanner({ job, crew }: { job: Job; crew: readonly JobAssignment[] }) {
  const move = useSetJobStatus()
  if (job.status !== 'in_progress') return null
  const atWork = crew.some((a) => a.completion === 'assigned' || a.completion === 'in_progress')
  const finished = crew.some((a) => a.completion === 'completed')
  if (atWork || !finished) return null

  const allHoursIn = job.estimatedHours !== null && job.completedHours >= job.estimatedHours
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-xl border bg-surface px-4 py-3 shadow-chip">
      <span className="text-ui text-foreground">
        {allHoursIn ? 'Every hour of the work is in.' : 'The crew is settled — no one is at work on this job anymore.'}{' '}
        <span className="text-muted-foreground">The job can be completed.</span>
      </span>
      <span className="flex-1" />
      <Button size="sm" variant="primary" busy={move.isPending} onClick={() => move.mutate({ id: job.id, status: 'completed' })}>
        Mark the Job Completed
      </Button>
      {move.error ? <p className="w-full text-caption text-danger">{describeError(move.error)}</p> : null}
    </div>
  )
}

function AssignForm({
  jobId,
  job,
  candidates,
  onDone,
  onCancel,
}: {
  jobId: string
  job: Job
  candidates: DirectoryAccount[]
  onDone: () => void
  onCancel: () => void
}) {
  const [playerId, setPlayerId] = useState('')
  const [amount, setAmount] = useState(() => job.playerRateAmount === null ? '' : String(job.playerRateAmount / 100))
  const [hours, setHours] = useState('')
  const [notes, setNotes] = useState('')
  const [notify, setNotify] = useState<NotifyTarget>('assigned')
  const assign = useAssignPlayer()

  const selected = candidates.find((player) => player.id === playerId) ?? null
  const clockWord = (player: DirectoryAccount): string =>
    player.availability === 'active' ? 'Logged In' : 'Logged Out'

  const amountInvalid = amount.trim() !== '' && !Number.isFinite(Number(amount))
  const hoursInvalid = hours.trim() !== '' && !Number.isFinite(Number(hours))
  const remaining = job.estimatedHours === null ? null : job.estimatedHours - job.assignedHours

  return (
    <motion.form
      {...FADE}
      onSubmit={(event) => {
        event.preventDefault()
        assign.mutate(
          {
            jobId,
            input: {
              playerId,
              ...(amount.trim() !== '' ? { paymentAmount: Math.round(Number(amount) * 100) } : {}),
              ...(hours.trim() !== '' ? { estimatedHours: Number(hours) } : {}),
              ...(notes.trim() !== '' ? { notes: notes.trim() } : {}),
              ...(job.status === 'draft' ? { notify: 'none' as const } : notify !== 'assigned' ? { notify } : {}),
            },
          },
          { onSuccess: onDone },
        )
      }}
      className="rounded-xl border bg-surface px-4 py-3 shadow-chip"
    >
      <div className="grid gap-x-6 gap-y-4 sm:grid-cols-2">
        <Field label="Player" htmlFor="assign-player">
          <Dropdown
            id="assign-player"
            ariaLabel="Player"
            placeholder="Choose a player"
            value={playerId}
            options={candidates.map((player) => ({
              value: player.id,
              label: personName(player),
              description: [...personDetails(player), clockWord(player)].filter(Boolean).join(' · '),
            }))}
            onChange={setPlayerId}
          />
          {selected && selected.availability === 'inactive' ? (
            <p className="mt-1.5 text-caption text-muted-foreground">
              {personName(selected)} is logged out. They are not taking new work right now, but you can
              still assign this and they will see it when they clock in.
            </p>
          ) : null}
        </Field>
        <Field
          label="Payment"
          htmlFor="assign-amount"
          hint={job.playerRateAmount === null
            ? `What this player is paid for the work (${job.currency ?? 'BDT'}).`
            : `From the job's player rate (${job.currency ?? 'BDT'}${job.playerRateType === 'hourly' ? ' per hour' : ''}). Change it for this player if needed.`}
          error={amountInvalid ? 'Numbers only — e.g. 1500.' : undefined}
        >
          <TextField
            id="assign-amount"
            inputMode="decimal"
            placeholder="e.g. 1500"
            value={amount}
            onChange={(event) => setAmount(event.target.value)}
          />
        </Field>
        <Field
          label="Hours"
          htmlFor="assign-hours"
          hint={
            remaining === null
              ? 'How much of the work is this player’s hand.'
              : remaining > 0
                ? `${formatHours(remaining)} of the ${formatHours(job.estimatedHours)} are still unassigned.`
                : 'The pool is already fully assigned — assigning beyond it oversubscribes the work.'
          }
          error={hoursInvalid ? 'Numbers only — e.g. 12.' : undefined}
        >
          <TextField
            id="assign-hours"
            inputMode="decimal"
            placeholder="e.g. 12"
            value={hours}
            onChange={(event) => setHours(event.target.value)}
          />
        </Field>
        <Field label="Notes" htmlFor="assign-notes">
          <TextField
            id="assign-notes"
            placeholder="What this player needs to know"
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
          />
        </Field>
        {job.status === 'draft' ? <p className="sm:col-span-2 text-caption text-muted-foreground">This player will be assigned privately. They will be notified when the job is published.</p> : <Field
          label="Who hears about it"
          htmlFor="assign-notify"
          hint="The notification that goes out the moment you assign."
          className="sm:col-span-2"
        >
          <Segmented<NotifyTarget>
            label="Notification audience"
            value={notify}
            onChange={setNotify}
            options={[
              { value: 'assigned', label: 'The assigned player' },
              { value: 'everyone', label: 'Every player' },
              { value: 'none', label: 'No one' },
            ]}
          />
        </Field>}
      </div>
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <Button type="submit" variant="primary" size="md" busy={assign.isPending} disabled={!playerId || amountInvalid || hoursInvalid}>
          Assign
        </Button>
        <Button type="button" size="md" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
        {assign.error ? <p className="text-caption text-danger">{describeError(assign.error)}</p> : null}
      </div>
    </motion.form>
  )
}

function AssignmentRow({ jobId, assignment }: { jobId: string; assignment: JobAssignment }) {
  const [open, setOpen] = useState(false)
  const state = partState(assignment)
  const atRest = assignment.completion === 'completed' || assignment.completion === 'dropped' || assignment.completion === 'removed'
  // The hours that count for this hand: the allocation while it is held,
  // what the work actually consumed once it is done.
  const hours =
    assignment.completion === 'completed'
      ? (assignment.actualHours ?? assignment.estimatedHours)
      : assignment.estimatedHours

  return (
    <li className="border-b border-hairline last:border-0">
      <button
        type="button"
        onClick={() => setOpen((current) => !current)}
        className="-mx-2 flex w-full items-center gap-3 rounded-md px-2 py-3 text-left transition-colors duration-fast hover:bg-tint"
      >
        <span
          className={cn(
            'text-muted-foreground transition-transform duration-fast',
            open ? 'rotate-90' : '',
          )}
        >
          <Icons.disclosure />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-ui font-medium text-foreground">
            {personName(playerOf(assignment))}
          </span>
          <span className="block truncate text-caption text-muted-foreground">
            {personDetails(playerOf(assignment)).join(' · ')}
          </span>
        </span>
        {hours !== null && hours !== undefined ? (
          <span
            className="font-mono text-[13px] text-muted-foreground"
            title={assignment.completion === 'completed' ? 'Hours completed' : 'Hours assigned'}
          >
            {formatHours(hours)}
          </span>
        ) : null}
        {assignment.paymentAmount !== null ? (
          <span className="font-mono text-[13px] text-muted-foreground" title="Payment">
            {formatMoney(assignment.paymentAmount)}
          </span>
        ) : null}
        <StatusLabel tone={PAYMENT_STATUS[assignment.paymentStatus].tone} label={PAYMENT_STATUS[assignment.paymentStatus].label} />
        <StatusLabel tone={state.tone} hue={state.hue} label={state.label} />
      </button>
      <AnimatePresence initial={false}>
        {open ? (
          <motion.div {...FADE} className="px-2 pb-4 pt-1">
            <CompletionVerbs jobId={jobId} assignment={assignment} />
            <AssignmentEditor jobId={jobId} assignment={assignment} atRest={atRest} />
            <HourAdjustment jobId={jobId} assignment={assignment} />
            <AssignmentNotes assignmentId={assignment.id} />
          </motion.div>
        ) : null}
      </AnimatePresence>
    </li>
  )
}

/** The assignment's own machine: the endings, and the way back from all of them. */
function CompletionVerbs({ jobId, assignment }: { jobId: string; assignment: JobAssignment }) {
  const move = useSetAssignmentCompletion()
  const [ending, setEnding] = useState<AssignmentCompletion | null>(null)
  const [reason, setReason] = useState('')
  const busy = move.isPending

  const act = (target: AssignmentCompletion, droppedReason?: string) =>
    move.mutate({ jobId, assignmentId: assignment.id, input: { completion: target, ...(droppedReason ? { droppedReason } : {}) } }, {
      onSuccess: () => {
        setEnding(null)
        setReason('')
      },
    })

  // Marking the hours complete names them: the verb says what will count.
  const verbLabel = (verb: { target: AssignmentCompletion; verb: string }): string =>
    verb.target === 'completed' && assignment.estimatedHours !== null
      ? `Mark ${formatHours(assignment.estimatedHours)} Completed`
      : verb.verb

  return (
    <div className="mb-4 rounded-xl border bg-surface-sunken px-4 py-3">
      <div className="flex flex-wrap items-center gap-2">
        {VERBS[assignment.completion].length === 0 ? (
          <p className="text-caption text-muted-foreground">The work is done and the record is final.</p>
        ) : null}
        {VERBS[assignment.completion].filter((verb) => verb.target !== 'removed').map((verb) =>
          verb.asksReason ? (
            <Button key={verb.target} size="xs" variant={verb.primary ? 'primary' : 'ghost'} busy={busy} onClick={() => setEnding(verb.target)}>
              {verb.verb}
            </Button>
          ) : (
            <Button
              key={verb.target}
              size="xs"
              variant={verb.primary ? 'primary' : 'ghost'}
              busy={busy}
              onClick={() => act(verb.target)}
            >
              {verbLabel(verb)}
            </Button>
          ),
        )}
        {VERBS[assignment.completion].some((verb) => verb.target === 'removed') ? (
          <Button size="sm" variant="danger" className="ml-auto" busy={busy} onClick={() => setEnding('removed')}>
            Remove
          </Button>
        ) : null}
      </div>
      {ending ? (
        <form
          className="mt-3 flex flex-wrap items-end gap-3"
          onSubmit={(event) => {
            event.preventDefault()
            act(ending, reason.trim() || undefined)
          }}
        >
          <Field
            label={ending === 'dropped' ? 'Why is the work dropped?' : 'Why is the player removed?'}
            htmlFor={`reason-${assignment.id}`}
            className="min-w-56 flex-1"
          >
            <TextField id={`reason-${assignment.id}`} value={reason} onChange={(event) => setReason(event.target.value)} />
          </Field>
          <Button type="submit" size="md" variant={ending === 'removed' ? 'danger' : 'primary'} busy={busy} disabled={ending === 'dropped' && !reason.trim()}>
            {ending === 'removed' ? 'Remove player' : 'Confirm'}
          </Button>
          <Button type="button" size="md" variant="ghost" onClick={() => setEnding(null)}>
            Cancel
          </Button>
        </form>
      ) : null}
      {move.error ? <p className="mt-2 text-caption text-danger">{describeError(move.error)}</p> : null}
    </div>
  )
}

/**
 * The working record for one player on one job: the money they are owed, the
 * hours, the quality of what came back. Everything here is editable mid-flight;
 * only what changed is sent. The payment *date* and status are the studio's
 * working note — the ledger of the next phase is the money truth.
 */
function AssignmentEditor({ jobId, assignment, atRest }: { jobId: string; assignment: JobAssignment; atRest: boolean }) {
  const [form, setForm] = useState(() => ({
    amount: assignment.paymentAmount === null ? '' : String(assignment.paymentAmount / 100),
    paymentStatus: assignment.paymentStatus,
    paymentDate: assignment.paymentDate ?? '',
    estimatedHours: assignment.estimatedHours === null ? '' : String(assignment.estimatedHours),
    quality: assignment.quality === null ? '' : String(assignment.quality),
    qualityNotes: assignment.qualityNotes ?? '',
    notes: assignment.notes ?? '',
  }))
  const update = useUpdateAssignment()
  const set = (key: keyof typeof form, value: string) => setForm((current) => ({ ...current, [key]: value }))

  const amountInvalid = form.amount.trim() !== '' && !Number.isFinite(Number(form.amount))
  const hoursInvalid = (key: 'estimatedHours') =>
    form[key].trim() !== '' && (!Number.isFinite(Number(form[key])) || Number(form[key]) < 0)
  const counted = assignment.actualHours ?? (assignment.completion === 'completed' ? assignment.estimatedHours ?? 0 : 0)
  const belowLogged = form.estimatedHours.trim() !== '' && !hoursInvalid('estimatedHours') &&
    Number(form.estimatedHours) < counted

  const submit = () => {
    const input: Record<string, unknown> = {}
    const money = form.amount.trim() === '' ? null : Math.round(Number(form.amount) * 100)
    if (money !== assignment.paymentAmount) input.paymentAmount = money
    if (form.paymentStatus !== assignment.paymentStatus) input.paymentStatus = form.paymentStatus
    const paymentDate = form.paymentDate.trim() === '' ? null : form.paymentDate.trim()
    if (paymentDate !== assignment.paymentDate) input.paymentDate = paymentDate
    for (const key of ['estimatedHours'] as const) {
      const value = form[key].trim() === '' ? null : Number(form[key])
      if (value !== assignment[key]) input[key] = value
    }
    const quality = form.quality === '' ? null : Number(form.quality)
    if (quality !== assignment.quality) input.quality = quality
    if (form.qualityNotes.trim() !== (assignment.qualityNotes ?? '')) {
      input.qualityNotes = form.qualityNotes.trim() === '' ? null : form.qualityNotes.trim()
    }
    if (form.notes.trim() !== (assignment.notes ?? '')) {
      input.notes = form.notes.trim() === '' ? null : form.notes.trim()
    }
    if (Object.keys(input).length === 0) return
    update.mutate({ jobId, assignmentId: assignment.id, input: input as UpdateAssignmentInput })
  }

  return (
    <form
      className="grid gap-x-6 gap-y-4 sm:grid-cols-2"
      onSubmit={(event) => {
        event.preventDefault()
        submit()
      }}
    >
      <Field
        label="Payment"
        htmlFor={`amount-${assignment.id}`}
        hint="In the job's currency."
        error={amountInvalid ? 'Numbers only — e.g. 1500.' : undefined}
      >
        <TextField
          id={`amount-${assignment.id}`}
          inputMode="decimal"
          value={form.amount}
          onChange={(event) => set('amount', event.target.value)}
        />
      </Field>
      <Field label="Payment status" htmlFor={`pay-status-${assignment.id}`}>
        <Dropdown
          id={`pay-status-${assignment.id}`}
          ariaLabel="Payment status"
          value={form.paymentStatus}
          options={[
            { value: 'unpaid', label: 'Unpaid' },
            { value: 'pending', label: 'Payment pending' },
            { value: 'paid', label: 'Paid' },
          ]}
          onChange={(value) => set('paymentStatus', value)}
        />
      </Field>
      <Field label="Payment date" htmlFor={`pay-date-${assignment.id}`}>
        <TextField
          id={`pay-date-${assignment.id}`}
          placeholder="YYYY-MM-DD"
          value={form.paymentDate}
          onChange={(event) => set('paymentDate', event.target.value)}
        />
      </Field>
      <Field
        label="Assigned Hours"
        htmlFor={`hours-${assignment.id}`}
        hint={`${formatHours(counted)} logged or counted as completed.`}
        error={belowLogged ? `Cannot assign fewer than ${formatHours(counted)} credited hours.` : undefined}
      >
        <div className="flex items-center gap-2">
          <TextField
            id={`hours-${assignment.id}`}
            inputMode="decimal"
            placeholder="Assigned"
            value={form.estimatedHours}
            onChange={(event) => set('estimatedHours', event.target.value)}
          />
        </div>
      </Field>
      {atRest ? (
        <>
          <Field label="Quality" htmlFor={`quality-${assignment.id}`} hint="Rate the work from 1 to 5.">
            <Dropdown
              id={`quality-${assignment.id}`}
              ariaLabel="Quality"
              value={form.quality}
              options={[
                { value: '', label: 'Not set' },
                ...[1, 2, 3, 4, 5].map((n) => ({ value: String(n), label: `${n} of 5` })),
              ]}
              onChange={(value) => set('quality', value)}
            />
          </Field>
          <Field label="Quality notes" htmlFor={`quality-notes-${assignment.id}`}>
            <TextField
              id={`quality-notes-${assignment.id}`}
              value={form.qualityNotes}
              onChange={(event) => set('qualityNotes', event.target.value)}
            />
          </Field>
        </>
      ) : null}
      <Field label="Notes" htmlFor={`notes-${assignment.id}`} className="sm:col-span-2">
        <TextArea id={`notes-${assignment.id}`} rows={2} value={form.notes} onChange={(event) => set('notes', event.target.value)} />
      </Field>
      <div className="flex flex-wrap items-center gap-3 sm:col-span-2">
        <Button
          type="submit"
          size="md"
          variant="primary"
          busy={update.isPending}
          disabled={amountInvalid || hoursInvalid('estimatedHours') || belowLogged}
        >
          Save Changes
        </Button>
        {update.error ? <p className="text-caption text-danger">{describeError(update.error)}</p> : null}
      </div>
    </form>
  )
}

function HourAdjustment({ jobId, assignment }: { jobId: string; assignment: JobAssignment }) {
  const isAdmin = useSession().data?.account?.role === 'admin'
  const history = useAssignmentHourAdjustments(jobId, assignment.id)
  const deduct = useDeductAssignmentHours()
  const credited = assignment.actualHours ?? (assignment.completion === 'completed' ? assignment.estimatedHours ?? 0 : 0)
  const [open, setOpen] = useState(false)
  const [hours, setHours] = useState(0)
  const [reason, setReason] = useState('')
  const [evidenceUrl, setEvidenceUrl] = useState('')
  const validUrl = !evidenceUrl.trim() || /^https?:\/\//i.test(evidenceUrl.trim())
  return (
    <section className="mt-5 border-t border-hairline pt-4">
      <div className="flex flex-wrap items-center gap-3">
        <p className="text-caption text-muted-foreground">Logged: <strong className="text-foreground">{formatHours(credited)}</strong></p>
        {isAdmin && credited > 0 && assignment.completion !== 'removed' && assignment.completion !== 'dropped' ? (
          <Button size="xs" variant="ghost" onClick={() => setOpen((value) => !value)}>{open ? 'Close Adjustment' : 'Adjust Logged Hours'}</Button>
        ) : null}
      </div>
      {open && isAdmin ? (
        <form className="mt-3 rounded-xl border bg-surface-sunken p-4" onSubmit={(event) => {
          event.preventDefault()
          if (hours <= 0 || hours > credited || !reason.trim() || !validUrl) return
          deduct.mutate({ jobId, assignmentId: assignment.id, input: { hours, reason: reason.trim(), ...(evidenceUrl.trim() ? { evidenceUrl: evidenceUrl.trim() } : {}) } }, {
            onSuccess: () => { setOpen(false); setHours(0); setReason(''); setEvidenceUrl('') },
          })
        }}>
          <p className="text-ui font-medium">Deduct disputed hours</p>
          <p className="mt-1 text-caption text-muted-foreground">Assigned hours stay at {assignment.estimatedHours === null ? 'open ended' : formatHours(assignment.estimatedHours)}. Completed work reopens for the player.</p>
          <div role="group" aria-label="Hours to deduct" className="mt-3 flex w-fit items-center gap-2 rounded-full border bg-surface p-1">
            <Button size="xs" variant="ghost" disabled={hours <= 0} onClick={() => setHours(Math.max(0, hours - 1))}>−</Button>
            <output className="min-w-12 text-center font-mono text-caption">{formatHours(hours)}</output>
            <Button size="xs" variant="ghost" disabled={hours >= credited} onClick={() => setHours(Math.min(credited, hours + 1))}>+</Button>
          </div>
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            <Field label="Reason for the player" htmlFor={`adjust-reason-${assignment.id}`} className="sm:col-span-2">
              <TextArea id={`adjust-reason-${assignment.id}`} rows={2} maxLength={1000} value={reason} onChange={(event) => setReason(event.target.value)} />
            </Field>
            <Field label="Evidence link (optional)" htmlFor={`adjust-evidence-${assignment.id}`} error={!validUrl ? 'Use an https:// link.' : undefined}>
              <TextField id={`adjust-evidence-${assignment.id}`} inputMode="url" value={evidenceUrl} onChange={(event) => setEvidenceUrl(event.target.value)} />
            </Field>
          </div>
          <div className="mt-3 flex items-center gap-3">
            <Button type="submit" size="md" variant="danger" busy={deduct.isPending} disabled={hours <= 0 || hours > credited || !reason.trim() || !validUrl}>Apply Deduction</Button>
            {deduct.error ? <p className="text-caption text-danger">{describeError(deduct.error)}</p> : null}
          </div>
        </form>
      ) : null}
      {history.data?.length ? (
        <div className="mt-4">
          <p className="text-caption font-medium">Hour adjustment history</p>
          <ul className="mt-2 space-y-2">
            {history.data.map((entry) => <li key={entry.id} className="text-caption text-muted-foreground">
              {formatTimestamp(entry.createdAt)} · {entry.actorName ?? 'Admin'} · {formatHours(entry.from)} → {formatHours(entry.to)}. {entry.reason}
              {entry.evidenceUrl ? <> · <a className="underline" href={entry.evidenceUrl} target="_blank" rel="noopener noreferrer">Evidence</a></> : null}
            </li>)}
          </ul>
        </div>
      ) : null}
    </section>
  )
}

/** The words on one assignment — the player's notes from their task among them. Shown only when there are any. */
function AssignmentNotes({ assignmentId }: { assignmentId: string }) {
  const notes = useNotes('assignment', assignmentId)
  if (!notes.data?.length) return null
  return (
    <section className="mt-4">
      <p className="text-micro text-muted-foreground uppercase">Notes</p>
      <ul className="mt-1.5 flex flex-col gap-2">
        {notes.data.map((note) => (
          <li key={note.id} className="rounded-xl bg-tint px-3 py-2">
            <p className="text-caption text-muted-foreground">
              {note.authorName ?? 'Someone'} · {formatTimestamp(note.createdAt)}
            </p>
            <p className="mt-0.5 whitespace-pre-wrap text-ui text-foreground">{note.body}</p>
          </li>
        ))}
      </ul>
    </section>
  )
}
